import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { MoveFolderEntityRequest, MoveFolderEntityResponse } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export const useMoveFolderEntity = (authToken: string) => {
  const { workspaceId } = useParams();
  const queryClient = useQueryClient();

  return useMutation<MoveFolderEntityResponse, Error, MoveFolderEntityRequest>({
    mutationFn: async (request: MoveFolderEntityRequest) => {
      const response = await fetch(ENDPOINTS.folder_entities(String(workspaceId)), {
        method: "POST",
        headers: jsonAuthHeaders(authToken),
        body: JSON.stringify(request),
      });
      const data: MoveFolderEntityResponse = await handleResponse(response);

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId, variables.folder_id],
      });

      // Also invalidate the source folders
      const entityTypes = Object.keys(variables.entities);
      entityTypes.forEach(() => {
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId],
        });
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      displayErrorToast(
        typeof error === "string" ? error : (error?.message ?? "Unknown error occurred"),
      );
    },
  });
};
